// =====================================================
// COMPONENTE: BypassCerticamara (Botón de Pánico)
// =====================================================
// Vidrio protector que se rompe + Botón pulsante para
// activar/desactivar bypass de Certicámara en emergencias.
// Solo accesible por superadmin.
// =====================================================

import React, { useState, useEffect } from 'react';
import { Alert, Spinner, Badge, Modal, Button, Form } from 'react-bootstrap';

const API_BASE = process.env.REACT_APP_NODE_ENV === 'production'
  ? process.env.REACT_APP_API_URL_PRODUCTION
  : 'http://localhost:3001';

const BypassCerticamara = ({ onSuccess }) => {
  // =====================================================
  // ESTADO
  // =====================================================
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [procesando, setProcesando] = useState(false);
  
  // Modal activar
  const [showModalActivar, setShowModalActivar] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [vidrioRoto, setVidrioRoto] = useState(false);
  
  // Modal desactivar
  const [showModalDesactivar, setShowModalDesactivar] = useState(false);
  const [observacion, setObservacion] = useState('');
  
  // Historial
  const [showHistorial, setShowHistorial] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  // =====================================================
  // CARGAR ESTADO INICIAL
  // =====================================================
  useEffect(() => {
    cargarEstado();
    // Refrescar cada 30 segundos para que el contador de "minutos activo" se actualice
    const interval = setInterval(cargarEstado, 30000);
    return () => clearInterval(interval);
  }, []);

  const cargarEstado = async () => {
    try {
      setError(null);
      const correo = localStorage.getItem('correo');
      const res = await fetch(`${API_BASE}/api/bypass/estado`, {
        headers: { 'usuario-correo': correo || '' }
      });
      const data = await res.json();
      if (data.success) setEstado(data.data);
      else setError(data.error || 'Error al consultar estado');
    } catch (err) {
      console.error('Error:', err);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // ROMPER VIDRIO
  // =====================================================
  const handleRomperVidrio = () => {
    setVidrioRoto(true);
    // Sonido de cristal rompiéndose (opcional)
    setTimeout(() => {
      setShowModalActivar(true);
    }, 600);
  };

  const cerrarModalActivar = () => {
    setShowModalActivar(false);
    setMotivo('');
    setTimeout(() => setVidrioRoto(false), 300);
  };

  // =====================================================
  // ACTIVAR BYPASS
  // =====================================================
  const handleActivar = async () => {
    if (motivo.trim().length < 5) {
      setError('El motivo debe tener al menos 5 caracteres');
      return;
    }
    try {
      setProcesando(true);
      setError(null);
      const correo = localStorage.getItem('correo');
      const res = await fetch(`${API_BASE}/api/bypass/activar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'usuario-correo': correo || '' },
        body: JSON.stringify({ motivo: motivo.trim() })
      });
      const data = await res.json();
      if (data.success) {
        onSuccess?.('🚨 Bypass activado correctamente');
        cerrarModalActivar();
        cargarEstado();
      } else {
        setError(data.error || 'Error al activar');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setProcesando(false);
    }
  };

  // =====================================================
  // DESACTIVAR BYPASS
  // =====================================================
  const handleDesactivar = async () => {
    if (observacion.trim().length < 5) {
      setError('La observación debe tener al menos 5 caracteres');
      return;
    }
    try {
      setProcesando(true);
      setError(null);
      const correo = localStorage.getItem('correo');
      const res = await fetch(`${API_BASE}/api/bypass/desactivar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'usuario-correo': correo || '' },
        body: JSON.stringify({ observacion: observacion.trim() })
      });
      const data = await res.json();
      if (data.success) {
        onSuccess?.(`✅ Bypass desactivado. Duración: ${data.data.duracionMinutos}min · Docs procesados: ${data.data.documentosProcesados}`);
        setShowModalDesactivar(false);
        setObservacion('');
        cargarEstado();
      } else {
        setError(data.error || 'Error al desactivar');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setProcesando(false);
    }
  };

  // =====================================================
  // HISTORIAL
  // =====================================================
  const cargarHistorial = async () => {
    setLoadingHistorial(true);
    try {
      const correo = localStorage.getItem('correo');
      const res = await fetch(`${API_BASE}/api/bypass/historial`, {
        headers: { 'usuario-correo': correo || '' }
      });
      const data = await res.json();
      if (data.success) setHistorial(data.data.historial);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingHistorial(false);
    }
  };

  useEffect(() => {
    if (showHistorial) cargarHistorial();
  }, [showHistorial]);

  // =====================================================
  // FORMATEAR TIEMPO
  // =====================================================
  const formatearTiempo = (minutos) => {
    if (minutos < 60) return `${minutos} min`;
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return `${h}h ${m}min`;
  };

  // =====================================================
  // RENDER LOADING
  // =====================================================
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <Spinner animation="border" variant="danger" />
        <span className="ms-3">Cargando estado del bypass...</span>
      </div>
    );
  }

  const activo = estado?.activo === true;

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================
  return (
    <div className="bypass-container">
      {/* ESTILOS */}
      <style>{`
        @keyframes pulseRed {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
          50% { box-shadow: 0 0 0 30px rgba(220, 53, 69, 0); }
        }
        @keyframes pulseGreen {
          0%, 100% { box-shadow: 0 0 0 0 rgba(25, 135, 84, 0.5); }
          50% { box-shadow: 0 0 0 20px rgba(25, 135, 84, 0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-3px) rotate(-1deg); }
          75% { transform: translateX(3px) rotate(1deg); }
        }
        @keyframes breakGlass {
          0% { opacity: 1; transform: scale(1); filter: blur(0); }
          50% { opacity: 0.7; transform: scale(1.05); filter: blur(1px); }
          100% { opacity: 0; transform: scale(1.2); filter: blur(8px); }
        }
        @keyframes warningBlink {
          0%, 50%, 100% { opacity: 1; }
          25%, 75% { opacity: 0.5; }
        }
        .panic-button-pulse-red {
          animation: pulseRed 1.5s infinite;
        }
        .panic-button-pulse-green {
          animation: pulseGreen 2.5s infinite;
        }
        .panic-warning-blink {
          animation: warningBlink 1.5s infinite;
        }
        .glass-protector {
          background: linear-gradient(135deg,
            rgba(255,255,255,0.4) 0%,
            rgba(200,225,255,0.3) 50%,
            rgba(255,255,255,0.4) 100%);
          backdrop-filter: blur(2px);
          border: 3px solid rgba(255,255,255,0.7);
          box-shadow:
            inset 0 0 20px rgba(255,255,255,0.5),
            0 0 30px rgba(0,0,0,0.1);
          transition: all 0.3s;
        }
        .glass-protector::before {
          content: '';
          position: absolute;
          top: 10%;
          left: 5%;
          width: 30%;
          height: 30%;
          background: linear-gradient(135deg, rgba(255,255,255,0.8), transparent);
          border-radius: 50%;
          filter: blur(8px);
          pointer-events: none;
        }
        .glass-broken {
          animation: breakGlass 0.6s forwards;
        }
        .panic-card {
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.3s;
        }
        .panic-card-active {
          background: linear-gradient(135deg, #fef2f2, #fee2e2);
          border: 2px solid #dc3545;
        }
        .panic-card-inactive {
          background: linear-gradient(135deg, #f0fdf4, #d1fae5);
          border: 2px solid #198754;
        }
      `}</style>

      {/* Header */}
      <div className="mb-4">
        <h5 className="mb-1">
          <i className="bi bi-exclamation-octagon-fill text-danger me-2"></i>
          Botón de Pánico — Bypass de Certicámara
        </h5>
        <small className="text-muted">
          Activa este botón SOLO cuando Certicámara esté fallando.
          Los documentos no recibirán firma digital mientras esté activo.
        </small>
      </div>

      {/* Mensaje de error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* TARJETA PRINCIPAL                                   */}
      {/* ═══════════════════════════════════════════════════ */}
      <div className={`panic-card ${activo ? 'panic-card-active' : 'panic-card-inactive'} p-4 mb-4`}>
        {/* Estado */}
        <div className="text-center mb-4">
          {activo ? (
            <>
              <div className="panic-warning-blink mb-2">
                <Badge bg="danger" style={{ fontSize: '1rem', padding: '10px 20px' }}>
                  🚨 BYPASS ACTIVO 🚨
                </Badge>
              </div>
              <h4 className="text-danger fw-bold mb-1">
                Certicámara DESACTIVADA temporalmente
              </h4>
              <p className="text-muted small mb-0">
                Los documentos se están firmando sin sello digital
              </p>
            </>
          ) : (
            <>
              <Badge bg="success" style={{ fontSize: '1rem', padding: '10px 20px' }}>
                ✅ OPERACIÓN NORMAL
              </Badge>
              <h4 className="text-success fw-bold mt-2 mb-1">
                Certicámara Activa
              </h4>
              <p className="text-muted small mb-0">
                Los documentos se firman digitalmente con normalidad
              </p>
            </>
          )}
        </div>

        {/* ═════════════════════════════════════ */}
        {/* BOTÓN GIGANTE                          */}
        {/* ═════════════════════════════════════ */}
        <div className="d-flex justify-content-center mb-3">
          <div style={{ position: 'relative', width: 280, height: 280 }}>
            
            {/* VIDRIO PROTECTOR (solo cuando NO está activo) */}
            {!activo && (
              <div
                className={`glass-protector ${vidrioRoto ? 'glass-broken' : ''}`}
                style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  borderRadius: '50%',
                  zIndex: 10,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={handleRomperVidrio}
              >
                <div className="text-center" style={{ pointerEvents: 'none' }}>
                  <div style={{ fontSize: '2.5rem' }}>🔨</div>
                  <div style={{ fontWeight: 700, color: '#dc3545', marginTop: 4 }}>
                    ROMPER VIDRIO
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Solo en emergencia
                  </div>
                </div>
              </div>
            )}

            {/* BOTÓN ROJO PULSANTE */}
            <button
              onClick={() => activo ? setShowModalDesactivar(true) : null}
              disabled={!activo && !vidrioRoto}
              className={activo ? 'panic-button-pulse-red' : 'panic-button-pulse-green'}
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                border: '8px solid',
                borderColor: activo ? '#7f1d1d' : '#14532d',
                background: activo
                  ? 'radial-gradient(circle at 30% 30%, #ef4444, #b91c1c 70%, #7f1d1d)'
                  : 'radial-gradient(circle at 30% 30%, #22c55e, #15803d 70%, #14532d)',
                color: 'white',
                fontWeight: 800,
                fontSize: '1.4rem',
                cursor: activo ? 'pointer' : 'default',
                position: 'relative',
                boxShadow: 'inset -8px -8px 20px rgba(0,0,0,0.3), inset 8px 8px 20px rgba(255,255,255,0.2)',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}
            >
              <div style={{ fontSize: '4rem', marginBottom: 8 }}>
                {activo ? '🚨' : '🟢'}
              </div>
              <div>{activo ? 'DESACTIVAR' : 'PÁNICO'}</div>
              <div style={{ fontSize: '0.7rem', fontWeight: 400, marginTop: 4, opacity: 0.85 }}>
                {activo ? 'Click para volver a normal' : 'Click para activar'}
              </div>
            </button>
          </div>
        </div>

        {/* DATOS DE LA SESIÓN ACTIVA */}
        {activo && estado && (
          <div className="mt-4 p-3 bg-white rounded-3 border border-danger">
            <h6 className="text-danger mb-3">
              <i className="bi bi-info-circle-fill me-2"></i>
              Información de la sesión actual
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <small className="text-muted d-block">Activado por:</small>
                <strong>{estado.activadoPor?.nombres || estado.activadoPor?.correo}</strong>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Activado el:</small>
                <strong>{estado.activadoEn ? new Date(estado.activadoEn).toLocaleString('es-CO') : '—'}</strong>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Tiempo activo:</small>
                <strong className="text-danger">⏱ {formatearTiempo(estado.minutosActivo)}</strong>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Docs procesados sin firma digital:</small>
                <strong className="text-warning">📄 {estado.documentosProcesadosSesion}</strong>
              </div>
              <div className="col-12">
                <small className="text-muted d-block">Motivo:</small>
                <em>"{estado.motivoActivacion}"</em>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════ */}
      {/* BOTÓN VER HISTORIAL                                 */}
      {/* ═══════════════════════════════════════════════════ */}
      <div className="text-center">
        <Button variant="outline-secondary" size="sm" onClick={() => setShowHistorial(true)}>
          <i className="bi bi-clock-history me-2"></i>
          Ver historial de activaciones
        </Button>
      </div>

      {/* ═══════════════════════════════════════════════════ */}
      {/* MODAL: ACTIVAR BYPASS                               */}
      {/* ═══════════════════════════════════════════════════ */}
      <Modal show={showModalActivar} onHide={cerrarModalActivar} centered backdrop="static">
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Confirmar activación del bypass
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>⚠️ Estás a punto de:</strong>
            <ul className="mb-0 mt-2 small">
              <li>Desactivar la firma digital de Certicámara</li>
              <li>Permitir que los documentos se firmen SOLO con BIOFIRMAS</li>
              <li>Quedar registrado como responsable de esta acción</li>
            </ul>
          </Alert>

          <Form.Group>
            <Form.Label className="fw-bold">
              Motivo de activación: <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: Certicámara no responde a las solicitudes y los webhooks no llegan desde las 10am..."
              maxLength={500}
              autoFocus
            />
            <Form.Text className="text-muted">
              Mínimo 5 caracteres · {motivo.length}/500
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cerrarModalActivar} disabled={procesando}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleActivar}
            disabled={procesando || motivo.trim().length < 5}
          >
            {procesando ? (
              <><Spinner size="sm" className="me-2" />Activando...</>
            ) : (
              <>🚨 ACTIVAR BYPASS</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════ */}
      {/* MODAL: DESACTIVAR BYPASS                            */}
      {/* ═══════════════════════════════════════════════════ */}
      <Modal show={showModalDesactivar} onHide={() => setShowModalDesactivar(false)} centered backdrop="static">
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="bi bi-check-circle-fill me-2"></i>
            Desactivar bypass
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success">
            <strong>✅ Estás reactivando la firma digital con Certicámara.</strong>
            <p className="mb-0 mt-2 small">
              Los siguientes documentos volverán a procesarse con firma digital normal.
            </p>
          </Alert>

          {estado && (
            <div className="mb-3 p-3 bg-light rounded">
              <small className="text-muted d-block">Resumen de esta sesión:</small>
              <div>⏱ Estuvo activo durante: <strong>{formatearTiempo(estado.minutosActivo)}</strong></div>
              <div>📄 Documentos sin firma digital: <strong>{estado.documentosProcesadosSesion}</strong></div>
            </div>
          )}

          <Form.Group>
            <Form.Label className="fw-bold">
              Observación de cierre: <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              placeholder="Ej: Certicámara confirmó que su incidente fue resuelto a las 4pm..."
              maxLength={500}
              autoFocus
            />
            <Form.Text className="text-muted">
              Mínimo 5 caracteres · {observacion.length}/500
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalDesactivar(false)} disabled={procesando}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleDesactivar}
            disabled={procesando || observacion.trim().length < 5}
          >
            {procesando ? (
              <><Spinner size="sm" className="me-2" />Desactivando...</>
            ) : (
              <>✅ DESACTIVAR BYPASS</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════ */}
      {/* MODAL: HISTORIAL                                    */}
      {/* ═══════════════════════════════════════════════════ */}
      <Modal show={showHistorial} onHide={() => setShowHistorial(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-clock-history me-2"></i>
            Historial de activaciones del bypass
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingHistorial ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : historial.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
              <p className="mt-2 mb-0">Aún no hay registros de activaciones</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Acción</th>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>Motivo / Observación</th>
                    <th className="text-center">Duración</th>
                    <th className="text-center">Docs</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((h, i) => (
                    <tr key={i}>
                      <td>
                        <Badge bg={h.accion === 'activado' ? 'danger' : 'success'}>
                          {h.accion === 'activado' ? '🚨 Activado' : '✅ Desactivado'}
                        </Badge>
                      </td>
                      <td className="small">
                        {new Date(h.fecha).toLocaleString('es-CO')}
                      </td>
                      <td className="small">
                        <strong>{h.realizadoPor?.nombres || '—'}</strong>
                        <br />
                        <span className="text-muted">{h.realizadoPor?.correo}</span>
                      </td>
                      <td className="small">{h.motivo}</td>
                      <td className="text-center small">
                        {h.duracionMinutos > 0 ? formatearTiempo(h.duracionMinutos) : '—'}
                      </td>
                      <td className="text-center small">
                        {h.documentosProcesados > 0 ? h.documentosProcesados : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistorial(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BypassCerticamara;